import type { ApiResponse, SearchAffiliateUsersResponse } from '@fluenceur/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import api from '@/libs/axiosInstance';

type AffiliateUserStatus = 'enrolled' | 'all';

interface SearchAffiliateUsersOptions {
  debounceMs?: number;
  enabledWhenEmpty?: boolean;
  limit?: number;
  status?: AffiliateUserStatus;
}

interface SearchAffiliateUsersFetchParams {
  limit: number;
  status: AffiliateUserStatus;
}

export const searchAffiliateUsers = async (
  query: string,
  { limit, status }: SearchAffiliateUsersFetchParams
): Promise<SearchAffiliateUsersResponse> => {
  const params = new URLSearchParams();
  if (query) {
    params.append('query', query);
  }
  params.append('limit', String(limit));
  params.append('status', status);

  const { data } = await api.get<ApiResponse<SearchAffiliateUsersResponse>>(
    `/dashboard/affiliates/search?${params.toString()}`
  );

  if (!data.data) {
    throw new Error('Invalid response structure');
  }

  return data.data;
};

export const useSearchAffiliateUsers = (query: string, opts: SearchAffiliateUsersOptions = {}) => {
  const { debounceMs = 300, enabledWhenEmpty = false, limit = 10, status = 'enrolled' } = opts;

  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce the query to avoid excessive API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [query, debounceMs]);

  const enabled = enabledWhenEmpty ? true : debouncedQuery.length >= 2;

  return useQuery<SearchAffiliateUsersResponse, Error>({
    queryKey: ['affiliates', 'search', limit, status, debouncedQuery],
    queryFn: () => searchAffiliateUsers(debouncedQuery, { limit, status }),
    enabled,
    // Keep the previous list visible while a new term resolves, so the picker's
    // dim-during-fetch treatment works instead of flashing empty.
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};
